import { useTranslation } from 'react-i18next';
import { Box, Icon, Stack, Text } from 'shell/components';
import { getAssignmentSubmissionTypes, getEvidenceInstructionEntries } from 'utils/assignmentSubmissionTypes';
import { getEvidenceSuggestionCatalog } from 'utils/submissionEvidenceSuggestions';
import EVIDENCE_TYPE_DETAILS from './evidenceTypeDetails';

const EvidenceInstructionList = ({ entries, label, t }) => (
    <Stack spacing={1}>
        <Text weight={'medium'}>{label}</Text>
        {entries.length ? (
            <Box
                component={'ul'}
                sx={{
                    m: 0,
                    pl: 5,
                    color: (theme) => theme.palette.ensightful.neutral[700],
                }}
            >
                {entries.map((entry, index) => (
                    <Text key={`${entry}-${index}`} color={'inherit'} component={'li'} variant={'p3'}>
                        {entry}
                    </Text>
                ))}
            </Box>
        ) : (
            <Text color={'light'} variant={'p3'}>
                {t('common:ui.none', 'None')}
            </Text>
        )}
    </Stack>
);

const SubmissionTypesImportPreview = ({ source }) => {
    const { i18n, t } = useTranslation('assignment');
    const settings = source?.ai_grading_settings || {};
    const types = getAssignmentSubmissionTypes({ gradingSettings: settings, api_data: source?.api_data });
    const preferences = settings.evidence_preferences || {};

    return (
        <Stack spacing={3}>
            {!types.length && <Text color={'light'}>{t('common:ui.none', 'None')}</Text>}
            {types.map((type) => {
                const config = EVIDENCE_TYPE_DETAILS[type];
                const label = t(`assignmentSettings:evidence.types.${type}.label`, config.label);
                const description = t(`assignmentSettings:evidence.types.${type}.description`, config.description);
                const parsingFlagSuggestions = getEvidenceSuggestionCatalog({
                    i18n,
                    type,
                    kind: 'flag',
                    fallbacks: config.flag,
                });
                const parsingIgnoreSuggestions = getEvidenceSuggestionCatalog({
                    i18n,
                    type,
                    kind: 'ignore',
                    fallbacks: config.ignore,
                });
                const sectionId = `submission-type-${type}`;

                return (
                    <Box key={type} component={'section'} aria-labelledby={`${sectionId}-label`}>
                        <Stack spacing={2}>
                            <Stack
                                alignItems={'center'}
                                direction={'row'}
                                flexWrap={'wrap'}
                                useFlexGap
                                spacing={2}
                                sx={{
                                    borderRadius: (theme) => theme.spacing(4),
                                    p: 4,
                                    background: (theme) => theme.palette.ensightful.neutral[50],
                                }}
                            >
                                <Stack
                                    alignItems={'center'}
                                    direction={'row'}
                                    flexShrink={0}
                                    sx={{ maxWidth: '100%', minWidth: 0 }}
                                    spacing={1}
                                >
                                    <Icon kind={config.icon} />
                                    <Text id={`${sectionId}-label`} component={'h3'} weight={'medium'}>
                                        {label}
                                    </Text>
                                </Stack>
                                <Text color={'light'} variant={'p3'} sx={{ minWidth: 0, flex: '1 1 200px' }}>
                                    {description}
                                </Text>
                            </Stack>
                            <Box sx={{ pl: 9 }}>
                                <Stack spacing={2}>
                                    <EvidenceInstructionList
                                        entries={getEvidenceInstructionEntries(
                                            preferences[type]?.flag_instructions,
                                            parsingFlagSuggestions
                                        )}
                                        label={t(
                                            'assignmentSettings:evidence.flag-label',
                                            'Should the AI flag these items?'
                                        )}
                                        t={t}
                                    />
                                    <EvidenceInstructionList
                                        entries={getEvidenceInstructionEntries(
                                            preferences[type]?.ignore_instructions,
                                            parsingIgnoreSuggestions
                                        )}
                                        label={t(
                                            'assignmentSettings:evidence.ignore-label',
                                            'What the AI should not flag'
                                        )}
                                        t={t}
                                    />
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
    );
};

export default SubmissionTypesImportPreview;
